"use client";

import { useResizeObserver } from "@faire/react/hooks/useResizeObserver";
import { mergeRefs } from "@faire/slate/lib/mergeRefs";
import React, { useCallback, useMemo, useState } from "react";

import { useModalContext } from "./ModalContext";
import { useModalScrollContext } from "./ModalScrollContext";

type IProps = React.HTMLAttributes<HTMLDivElement>;

export const ModalScrollArea = ({ children, ...props }: IProps) => {
  const { setContentHeight } = useModalContext();
  const { scrollAreaRef } = useModalScrollContext();
  const [isScrollable, setIsScrollable] = useState(false);

  const updateScrollable = useCallback(() => {
    const el = scrollAreaRef.current;
    if (!el) {
      return;
    }
    // 1px tolerance for sub-pixel rounding
    setIsScrollable(el.scrollHeight - el.clientHeight > 1);
  }, [scrollAreaRef]);

  const contentResizeRef = useResizeObserver<HTMLDivElement>((entry) => {
    setContentHeight(entry.target.clientHeight);
    updateScrollable();
  });

  const containerResizeRef = useResizeObserver<HTMLDivElement>(() => {
    updateScrollable();
  });

  const containerRef = useMemo(
    () => mergeRefs(scrollAreaRef, containerResizeRef),
    [scrollAreaRef, containerResizeRef]
  );

  return (
    <div
      {...props}
      ref={containerRef}
      className="relative min-h-0 flex-1 overflow-y-auto"
      tabIndex={isScrollable ? 0 : undefined}
    >
      <div ref={contentResizeRef}>{children}</div>
    </div>
  );
};
